import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getSurvey } from "../services/api";
import {
  formatDateTimeEDT,
  formatPhoneNumber,
  formatDate
} from "../utils/formatters";
import EffectivenessSlider from "../components/EffectivenessSlider";

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--gray-100);
  border-radius: 0.375rem;
  color: var(--gray-700);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: var(--gray-200);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PageTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--gray-900);
  margin: 0;
`;

const SurveyId = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: var(--gray-600);
`;

const SurveyDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-600);
`;

const SurveyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SurveySection = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* Use data attribute instead of prop for fullWidth */
  grid-column: ${(props) => (props["data-fullwidth"] ? "1 / -1" : "auto")};

  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-800);
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--gray-200);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    bottom: -1px;
    width: 40px;
    height: 3px;
    background-color: var(--primary);
    border-radius: 1.5px;
  }
`;

const Card = styled.div`
  padding: 1.5rem;
  background-color: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 0.375rem;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--gray-200);
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: var(--gray-900);
  font-weight: 500;
`;

const EffectivenessRating = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${(props) => {
    const rating = parseInt(props.value, 10);
    if (rating >= 8) return "var(--success)";
    if (rating >= 5) return "var(--warning)";
    if (rating >= 1) return "var(--danger)";
    return "var(--gray-400)";
  }};
  color: var(--gray-900);
  text-align: center;
  min-width: 40px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: ${(props) => {
    if (props.status === "success") return "var(--success)";
    if (props.status === "warning") return "var(--warning)";
    if (props.status === "neutral") return "var(--gray-400)";
    return "var(--danger)";
  }};
  color: var(--white);
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.25rem;
`;

const SeverityBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: ${(props) => {
    if (props.severity === "low") return "var(--success)";
    if (props.severity === "medium") return "var(--warning)";
    return "var(--danger)";
  }};
  color: var(--white);
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.25rem;
`;

const CopyIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary);
    background: var(--primary-light);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Tooltip = styled.span`
  position: absolute;
  background: var(--dark);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);

  &:after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: var(--dark) transparent transparent transparent;
  }
`;

function SurveyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSurvey();
  }, [id]);

  const fetchSurvey = async () => {
    setLoading(true);
    try {
      const data = await getSurvey(id);
      setSurvey(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching survey:", err);
      setError("Failed to load survey details");
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center py-8">
          <div className="animate-pulse">Loading survey details...</div>
        </div>
      </Container>
    );
  }

  if (error || !survey) {
    return (
      <Container>
        <div className="text-center py-8 text-red-600">
          <div>{error || "Survey not found"}</div>
          <Link
            to="/surveys"
            className="text-blue-500 hover:underline mt-4 inline-block"
          >
            Return to Surveys List
          </Link>
        </div>
      </Container>
    );
  }

  // Helper function to determine status badge color
  const getStatusBadgeType = (value) => {
    if (value === "Yes") return "warning";
    if (value === "No") return "success";
    if (
      value === "Sometimes" ||
      value === "Somewhat" ||
      value === "Occasionally"
    )
      return "warning";
    return "neutral";
  };

  return (
    <Container>
      <BackButton onClick={handleBackClick}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clipRule="evenodd"
          />
        </svg>
        Back
      </BackButton>

      <PageHeader>
        <div>
          <PageTitle>Survey Details</PageTitle>
          <SurveyId>ID: {survey.survey_id}</SurveyId>
        </div>
        <SurveyDate>
          <span>Submitted on</span>
          <strong>{formatDateTimeEDT(survey.created_at)}</strong>
        </SurveyDate>
      </PageHeader>

      <SurveyGrid>
        {/* Patient Information */}
        <SurveySection data-fullwidth>
          <SectionTitle>Patient Information</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Patient Name</InfoLabel>
              <InfoValue>
                <Link to={`/patients/${survey.patient.patient_id}`}>
                  {survey.patient.first_name} {survey.patient.last_name}
                </Link>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>
                <a
                  href={`mailto:${survey.patient.email}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCopyEmail(survey.patient.email);
                  }}
                >
                  {survey.patient.email}
                  {copied ? " (Copied!)" : " (Click to copy)"}
                </a>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>
                <a href={`tel:${survey.patient.phone}`}>
                  {formatPhoneNumber(survey.patient.phone)}
                </a>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Gender</InfoLabel>
              <InfoValue>{survey.patient.gender || "N/A"}</InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Medicine Information */}
        <SurveySection>
          <SectionTitle>💊 Medication & Dosage Details</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Current Medicine</InfoLabel>
              <InfoValue>{survey.current_medicine || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Starting Dose</InfoLabel>
              <InfoValue>{survey.starting_dose || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Current Dose</InfoLabel>
              <InfoValue>{survey.current_dose || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Last Dose Increase</InfoLabel>
              <InfoValue>{survey.last_dose_increase || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Increasing Dosage</InfoLabel>
              <InfoValue>{survey.increasing_dosage || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Medicine Effectiveness</InfoLabel>
              <InfoValue>
                <EffectivenessRating value={survey.medicine_effectiveness || 0}>
                  {survey.medicine_effectiveness || "N/A"}/10
                </EffectivenessRating>
              </InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Weight Loss Progress & Goals */}
        <SurveySection>
          <SectionTitle>🏁 Weight Loss Progress & Goals</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Weight Loss Goal</InfoLabel>
              <InfoValue>{survey.weight_loss_goal || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Weight Loss (Last 30 Days)</InfoLabel>
              <InfoValue>
                {survey.weight_loss_in_last_30_days
                  ? `${survey.weight_loss_in_last_30_days} lbs`
                  : "N/A"}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Weight Location</InfoLabel>
              <InfoValue>{survey.weight_location || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Preserve Lean Muscle</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.preserve_lean_muscle)}
                >
                  {survey.preserve_lean_muscle || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Side Effects */}
        <SurveySection>
          <SectionTitle>⚠️ Side Effects & Symptoms</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Experienced Side Effects</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={
                    survey.experienced_side_effects === "Yes"
                      ? "warning"
                      : "success"
                  }
                >
                  {survey.experienced_side_effects || "No"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>

            {survey.experienced_side_effects === "Yes" && (
              <>
                <InfoRow>
                  <InfoLabel>Side Effects</InfoLabel>
                  <InfoValue>
                    {survey.side_effects || "Not specified"}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Interfering with Daily Life</InfoLabel>
                  <InfoValue>
                    {survey.side_effect_interfering || "N/A"}
                  </InfoValue>
                </InfoRow>
              </>
            )}
            <InfoRow>
              <InfoLabel>Fatigue with New Habits</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.fatigue_with_new_habits)}
                >
                  {survey.fatigue_with_new_habits || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Diet & Nutrition */}
        <SurveySection>
          <SectionTitle>🥑  Nutrition & Physical Activity</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Current Diet Description</InfoLabel>
              <InfoValue>{survey.current_diet_description || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Diet Changes</InfoLabel>
              <InfoValue>{survey.diet_changes || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Calorie Deficit/Fasting</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.calorie_deficit_fasting)}
                >
                  {survey.calorie_deficit_fasting || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>High Protein, Low Carb</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.high_protein_low_carb)}
                >
                  {survey.high_protein_low_carb || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Keto Diet</InfoLabel>
              <InfoValue>
                <StatusBadge status={getStatusBadgeType(survey.keto_diet)}>
                  {survey.keto_diet || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Plant-Based Diet</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.plant_based_diet)}
                >
                  {survey.plant_based_diet || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Reduced Processed Foods</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.reduced_processed_foods)}
                >
                  {survey.reduced_processed_foods || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Removed Animal Products</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.removed_animal_products)}
                >
                  {survey.removed_animal_products || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Reduced Protein Intake</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.reduced_protein_intake)}
                >
                  {survey.reduced_protein_intake || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            {survey.reduced_protein_intake === "Yes" && (
              <InfoRow>
                <InfoLabel>Reason for Reduced Protein</InfoLabel>
                <InfoValue>
                  {survey.protein_intake_reduction_reason || "Not specified"}
                </InfoValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoLabel>Cleaned Up Diet</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.cleaned_up_diet)}
                >
                  {survey.cleaned_up_diet || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Increased Alcohol/Caffeine</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.increased_alcohol_caffeine)}
                >
                  {survey.increased_alcohol_caffeine || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Physical Activity */}
        <SurveySection>
          <SectionTitle>🏋️‍♂️ Physical Activity</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Exercise Changes</InfoLabel>
              <InfoValue>{survey.exercise_changes || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Muscle Loss/Reduced Strength</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(
                    survey.muscle_loss_reduced_strength
                  )}
                >
                  {survey.muscle_loss_reduced_strength || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Strength Decline with Diet</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.strength_decline_with_diet)}
                >
                  {survey.strength_decline_with_diet || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Energy Changes with Diet</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.energy_changes_with_diet)}
                >
                  {survey.energy_changes_with_diet || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Medical Events & Conditions */}
        <SurveySection>
          <SectionTitle>🩺 Medical Events & Conditions</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>New Medical Conditions</InfoLabel>
              <InfoValue>{survey.new_medication_conditions || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>New Medications</InfoLabel>
              <InfoValue>{survey.new_medications || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Fatty Liver Concerns</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.fatty_liver_concerns)}
                >
                  {survey.fatty_liver_concerns || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Homocysteine Concerns</InfoLabel>
              <InfoValue>
                <StatusBadge
                  status={getStatusBadgeType(survey.homocysteine_concerns)}
                >
                  {survey.homocysteine_concerns || "N/A"}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Lifestyle & Habits */}
        <SurveySection>
          <SectionTitle>⏰ Lifestyle & Habits</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Sleep Hours</InfoLabel>
              <InfoValue>{survey.sleep_hours || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Eating Habit</InfoLabel>
              <InfoValue>{survey.eating_habit || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Cravings Time</InfoLabel>
              <InfoValue>{survey.cravings_time || "N/A"}</InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Medication Recommendations */}
        <SurveySection>
          <SectionTitle>💊 Medication Recommendations</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Dosage Instructions</InfoLabel>
              <InfoValue>{survey.dosage_instructions || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Suggested Medication</InfoLabel>
              <InfoValue>{survey.suggested_medication || "N/A"}</InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>

        {/* Shipping Information */}
        <SurveySection data-fullwidth>
          <SectionTitle>📦 Shipping Information</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Address Changed</InfoLabel>
              <InfoValue>{survey.address_changed || "N/A"}</InfoValue>
            </InfoRow>
            {survey.address_changed !== "No" && survey.new_shipping_address && (
              <InfoRow>
                <InfoLabel>New Shipping Address</InfoLabel>
                <InfoValue>{survey.new_shipping_address}</InfoValue>
              </InfoRow>
            )}
          </Card>
        </SurveySection>

        <SurveySection data-fullwidth>
          <SectionTitle>📦Summary</SectionTitle>
          <Card>
            <InfoRow>
              <InfoLabel>Ai Summary</InfoLabel>
              <InfoValue>{survey.ai_summary || "N/A"}</InfoValue>
            </InfoRow>
          </Card>
        </SurveySection>
      </SurveyGrid>
    </Container>
  );
}

export default SurveyDetailPage;
